import React, { useState, useMemo } from "react"
import withStyles from "react-jss"
import ingredientsData from "../../data/ingredients"
import cocktailData from "../../data/cocktails"
import { SMALL_MOBILE_BREAKPOINT } from "../../data/globalStyles"
import { useCombobox } from "downshift"
import colors from "../../data/colors"
import { closeIcon } from "../../assets"

const styles = {
  dropdownContainer: {
    width: "50%",
    minWidth: "340px",
    maxWidth: "620px",
    marginRight: "1rem",
    "@media only screen and (max-width: 920px)": {
      width: "100%",
      minWidth: "100%",
      maxWidth: "100%",
      marginRight: 0,
    },
  },
  searchInput: {
    boxSizing: "border-box",
    height: "4rem",
    maxHeight: "60px",
    backgroundColor: "rgba(255, 255, 255, 0.78)",
    color: colors.black,
    border: 0,
    borderBottom: `2px solid ${colors.orangeHighlight}`,
    fontFamily: "Futura",
    fontSize: "calc(14px + (16 - 14) * ((100vw - 300px) / (1600 - 300)))",
    fontWeight: 500,
    outline: "none",
    padding: "1rem 1.3rem",
    "&::placeholder": {
      fontFamily: "Futura",
      fontSize: "calc(12px + (16 - 12) * ((100vw - 300px) / (1600 - 300)))",
      fontStyle: "italic",
      color: colors.inputPlaceholder,
    },
    [`@media only screen and (max-width: ${SMALL_MOBILE_BREAKPOINT}px)`]: {
      maxHeight: "46px",
    },
    width: "100%",
  },
  menuContainer: {
    position: "relative",
    zIndex: 2,

    width: "100%",
  },
  menuStyles: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "99.5%",
    maxHeight: "20ch",
    overflow: "scroll",
    margin: "0 !important",
    padding: 0,
    border: `2px solid ${colors.orangeHighlight}`,
    borderTop: 0,

    backgroundColor: "#EBDECD",
  },
  menuItem: {
    listStyle: "none",
    margin: "0 !important",
    padding: "1rem 1.3rem",
    fontFamily: "Futura",
    fontWeight: 450,
    fontSize: "calc(14px + (16 - 14) * ((100vw - 300px) / (1600 - 300)))",
    color: colors.black,
    overflowWrap: "break-word",
    cursor: "pointer",
  },
  menuItemFirst: {
    listStyle: "none",
    margin: 0,
    padding: "1rem 1.3rem",
    fontFamily: "Futura",
    fontWeight: 700,
    fontSize: "calc(14px + (16 - 14) * ((100vw - 300px) / (1600 - 300)))",
    color: colors.black,
    overflowWrap: "break-word",
    cursor: "pointer",
  },
  menuItemBorder: {
    borderBottom: "1px solid #DCC19E",
    margin: "0 1.3em",
  },
  menuItemCategory: {
    fontSize: "calc(10px + (12 - 10) * ((100vw - 300px) / (1600 - 300)))",
    marginLeft: "0.8em",
    color: "#676767",
    textTransform: "uppercase",
  },
  searchBox: {
    display: "flex",
  },
  closeMobile: {
    width: "18px",
    height: "18px",
    margin: "0.9rem 0 0 0.2rem",
    "@media only screen and (max-width: 920px) and (min-width : 481px)": {
      margin: "1.2rem 0 0 0.2rem",
    },
    "@media only screen and (min-width: 921px)": {
      display: "none",
    },
  },
  error: {
    fontSize: "12px",
    fontStyle: "italic",
    color: "#D06434",
    textAlign: "center",
    display: "block",
  },
}

const Dropdown = ({
  classes,
  placeholder,
  value,
  errors,
  setErrors,
  dataIndex,
  closeButton,
  setData,
  deleteIngredient,
  handleDropdownChange,
}) => {
  const MENU_ITEMS = useMemo(
    () => [
      ...ingredientsData.map((d) => {
        return { name: d.name, category: d.category }
      }),
      ...cocktailData.map((d) => {
        return { name: d.name, category: d.category }
      }),
    ],
    []
  )
  const [inputItems, setInputItems] = useState(MENU_ITEMS)
  const [searchVal, setSearchVal] = useState("")
  const [showDropdown, setShowDropdown] = useState(true)

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
  } = useCombobox({
    items: inputItems,
    itemToString: (item) => item.name,
    selectedItem: value,
    onInputValueChange: ({ inputValue }) => {
      setErrors((prevState) => ({
        ...prevState,
        ingredientName: false,
      }))
      // Update state
      setSearchVal(inputValue)
      handleDropdownChange(inputValue, dataIndex)
      // Update dropdown options
      setInputItems(
        MENU_ITEMS.filter(
          (item) =>
            item.name.toLowerCase().includes(inputValue.trim().toLowerCase()) ||
            item.category
              .toLowerCase()
              .includes(inputValue.trim().toLowerCase())
        )
      )
    },
  })

  const BoldedText = (text, shouldBeBold) => {
    if (text) {
      const textArray = text.split(RegExp(shouldBeBold, "ig"))
      const match = text.match(RegExp(shouldBeBold, "ig"))

      return (
        <span>
          {textArray.map((item, index) => (
            <>
              {item}
              {index !== textArray.length - 1 && match && <b>{match[index]}</b>}
            </>
          ))}
        </span>
      )
    } else return null
  }

  const INITIAL_INGREDIENTS = {
    name: "",
    volume: "",
    abv: "",
  }

  return (
    <div className={classes.dropdownContainer} style={{ width: "53%" }}>
      <div {...getComboboxProps()} className={classes.searchBox}>
        <input
          {...getInputProps({
            placeholder: placeholder,
            value: value,
            onKeyDown: (event) => {
              if (event.keyCode === 13) {
                event.preventDefault()
                setShowDropdown(false)
              } else if (!showDropdown) {
                if (event.keyCode === 8 || event.keyCode === 46) {
                  setShowDropdown(true)
                }
              }
            },
          })}
          className={classes.searchInput}
        />
        {closeButton > 1 ? (
          <img
            className={classes.closeMobile}
            src={closeIcon}
            onClick={() => deleteIngredient(dataIndex)}
            alt="Remove the ingredient."
          />
        ) : (
          <img
            className={classes.closeMobile}
            src={closeIcon}
            onClick={() => setData([INITIAL_INGREDIENTS])}
            alt="Remove the ingredient."
          />
        )}
      </div>
      <div {...getMenuProps()} className={classes.menuContainer}>
        {showDropdown && isOpen ? (
          <ul className={classes.menuStyles}>
            <li
              className={classes.menuItemFirst}
              onClick={() => setShowDropdown(false)}>
              {searchVal}
            </li>
            <div className={classes.menuItemBorder}></div>
            {inputItems.map((item, index) => {
              return (
                <li
                  className={classes.menuItem}
                  style={
                    highlightedIndex === index
                      ? {
                          color: colors.orangeHighlight,
                        }
                      : {}
                  }
                  key={`${item.name}${index}`}
                  {...getItemProps({ item, index })}>
                  {BoldedText(item.name, searchVal)}
                  <span className={classes.menuItemCategory}>
                    {item.category}
                  </span>
                </li>
              )
            })}
          </ul>
        ) : null}
      </div>
      {errors && errors.ingredientName[dataIndex] && (
        <span className={classes.error}>choose at least one ingredient</span>
      )}
    </div>
  )
}

export default withStyles(styles)(Dropdown)
